/**
 * Resolve the persistent Territory Identity id for a parcel the CONNECTED wallet
 * owns — so the on-grid Parcel Profile can surface the canonical owner moderation
 * banner. Reuses the same bounded public discovery endpoint the map viewer uses;
 * returns null for non-owners / unnamed territories (no fabrication).
 */
import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { batchTerritoryIdentities } from "../../lib/brand-image-api";
import { normalizeOwnerIdentity } from "../../domain/territory";

export function useOwnedTerritoryIdentityId(
  parcel: { id: number | string; owner: string } | null,
): string | null {
  const { address } = useAccount();
  const [identityId, setIdentityId] = useState<string | null>(null);

  const pidRaw = parcel?.id ?? null;
  const owner = parcel?.owner ?? null;

  useEffect(() => {
    setIdentityId(null);
    if (pidRaw == null || !owner || !address) return;
    if (normalizeOwnerIdentity(address) !== normalizeOwnerIdentity(owner)) return;
    const pid = Number(pidRaw);
    if (!Number.isFinite(pid)) return;
    const c = new AbortController();
    batchTerritoryIdentities([pid], c.signal)
      .then((items) => {
        const ownerKey = normalizeOwnerIdentity(owner);
        const m = items.find(
          (i) => normalizeOwnerIdentity(i.owner) === ownerKey && i.member_parcel_ids.includes(pid),
        );
        setIdentityId(m?.identity_id ?? null);
      })
      .catch(() => {});
    return () => c.abort();
  }, [pidRaw, owner, address]);

  return identityId;
}
